import React, { Fragment } from 'react';
import { Flex, Box, Button, Heading, Text } from 'rebass';
import styled from 'styled-components';
import { withWizard } from 'react-albus';
import { Formik, Form } from 'formik';

import { TextInput } from '../TextInput';
import { FormField } from '../FormField';
import TextArea from '../TextArea';
import { withApollo, compose } from 'react-apollo';
import { withToastManager } from 'react-toast-notifications';
import { APPOINTMENTS, CHARTS, CURRENT_USER, SET_AS_INACTIVE } from '../../graphql';
import * as Yup from 'yup';
import ScrollAnimation from 'react-animate-on-scroll';
import { fadeInDown, fadeInUp } from '../../utils/animationsStyleBuilder';
import Select from 'react-select';

const StyledText = styled(Text)`
  line-height: 1.6;
  border-bottom: 1px solid;
  /* color: white; */
  text-align: center;

  &:hover {
    cursor: pointer;
    font-weight: 600;
  }
`;
const DesktopOnly = styled(Flex)`
  @media screen and (max-width: 639px) {
    display: none;
  }
`;

const MobileOnly = styled(Flex)`
  @media screen and (min-width: 640px) {
    display: none;
  }
`;
const schema = Yup.object().shape({
  glucose: Yup.number().positive(),
  hbA1c: Yup.string(),
});
const customStyles = {
  container: (provided) => ({
    ...provided,
    width: '100%',
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'white',
    height: '64px',
    borderRadius: '10px',
    border: `${state.focused ? '3' : '1'}px solid #44a5ff`,
    zIndex: 5,
    minWidth: '160px',
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const HBA1C_UNITS = [{ label: '%', value: '%' }, { label: 'mmol/mol', value: 'mmol/mol' }];

class Diabetes2GlucoseHbA1c extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
    };
  }

  formatSubmitGlucose = (values) => {
    let payload = [];
    payload.push({
      questionId: 'VITALS.BLOODSUGAR',
      answer: `${values.glucose}`,
      value: `${values.glucose}`,
    });

    return payload;
  };

  formatSubmitNote = (values) => {
    let payload = [];
    let h1 = null;
    if (values.hbA1c) {
      if (values.hbA1cUnit === '%') {
        h1 = values.hbA1c;
      } else {
        h1 = this.computeMmolToPercentage(values.hbA1c);
      }
    }
    payload.push({
      questionId: window._env_.REACT_APP_GRAPHQL_URL.indexOf('preview') > -1 || window._env_.REACT_APP_GRAPHQL_URL.indexOf('localhost') > -1 ? '425' : '471',
      answer: 'Y',
      value: `Glucose: ${values.glucose} mg/dL ; ${h1 ? `HbA1c: ${h1}%` : ``}`,
    });

    return payload;
  };

  formatSubmitHbA1c = (v) => {
    let payload = [];
    payload.push({
      questionId: 'ans_hba1c',
      answer: 'Y',
      value: `${v}`,
    });

    return payload;
  };

  computeMmolToPercentage = (hbA1c) => {
    if (!hbA1c || isNaN(hbA1c)) return null;
    return (hbA1c / 10.93 + 2.15).toFixed(2);
  };

  formatSubmit = (values) => {
    const { data } = this.props;
    const regex = /_/g;
    let payload = [];
    payload.push(
      {
        questionId: 'LOCAL.121',
        answer: `${values.feet}'${values.inches}"`,
      },
      {
        questionId: 'LOCAL.122',
        answer: `${values.pounds}`,
      }
    );

    return payload;
  };

  render() {
    const { data, onSubmit, wizard, onSubmitArray } = this.props;

    return (
      <Fragment>
        <Flex flex={1} flexDirection="column" style={{ zIndex: 1, paddingLeft: '8px', paddingRight: '8px' }}>
          <Box m={2} style={{ ...fadeInDown() }}>
            {/* <ScrollAnimation animateIn="fadeInDownFrom20" animateOnce={false}> */}
            <Text fontSize={'20px'} fontWeight={400} color="#344f77" textAlign="center">
              {data.prompt}
            </Text>
            {/* </ScrollAnimation> */}
          </Box>

          <Formik
            initialValues={{
              hbA1cUnit: '%',
            }}
            validationSchema={schema}
            onSubmit={(values, actions) => {
              if (!values.glucose && !values.hbA1c) {
                wizard.push(data.skip);
                return;
              }
              const formattedGlucose = this.formatSubmitGlucose(values);
              const formattedNote = this.formatSubmitNote(values);
              let h1 = null;

              if (values.hbA1c) {
                if (values.hbA1cUnit === '%') {
                  h1 = values.hbA1c;
                } else {
                  h1 = this.computeMmolToPercentage(values.hbA1c);
                }
              }
              const submit = [];
              if (values.glucose) {
                submit.push({
                  values: formattedGlucose,
                  endpoint: 'vitals',
                });
              }

              if (values.glucose || values.hbA1c) {
                submit.push({
                  values: formattedNote,
                  endpoint: 'medicalHistory',
                });
              }
              if (h1) {
                const formattedHbA1c = this.formatSubmitHbA1c(h1);
                if (formattedHbA1c) {
                  submit.push({
                    values: formattedHbA1c,
                    endpoint: 'medicalHistory',
                  });
                }
              }
              onSubmitArray(submit, actions, wizard, data);
            }}
            render={({ isValid, errors, touched, values, setFieldValue }) => (
              <Form>
                <Flex flex={1} flexDirection="column" style={{ zIndex: 1, alignItems: 'center' }}>
                  <Flex my={3} flex={1} flexDirection="column">
                    <Flex mb={3} flexDirection="column" alignItems="center" style={{ ...fadeInUp(20, '1.1s', '100ms') }}>
                      <Text mb={2}>{`What was your most recent fasting glucose value (in mg/dL)?`}</Text>
                      <FormField containerStyle={{ flex: 'initial' }} error={touched['glucose'] && errors['glucose']} alignSelf="center">
                        <TextInput containerStyle={{ flex: 'initial' }} isAsyncMode={true} name={'glucose'} type={'number'} error={errors} inputMode="numeric" />
                      </FormField>
                    </Flex>

                    <Flex my={4} flexDirection="column" alignItems="center" style={{ ...fadeInUp(20, '1.2s', '200ms') }}>
                      <Text mb={2}>{`What was your most recent hemoglobin A1c (HbA1c) value (with units)?`}</Text>
                      <Flex flexDirection={['column', 'row']}>
                        <FormField containerStyle={{ flex: 'initial' }} error={touched['hbA1c'] && errors['hbA1c']} alignSelf="center">
                          <TextInput containerStyle={{ flex: 'initial' }} isAsyncMode={true} name={'hbA1c'} type={'number'} error={errors} mr={3} inputMode="numeric" />
                        </FormField>
                        <DesktopOnly>
                          <Flex style={{ minWidth: '100px', zIndex: 1 }} ml={[0, 3]}>
                            <Select
                              classNamePrefix="region-select"
                              styles={customStyles}
                              options={HBA1C_UNITS}
                              value={HBA1C_UNITS.find((x) => x.value === values.hbA1cUnit)}
                              onChange={(option) => {
                                if (!option || !option.value) return;
                                setFieldValue('hbA1cUnit', option.value);
                              }}
                            />
                          </Flex>
                        </DesktopOnly>
                        <MobileOnly>
                          <Flex style={{ width: '100%', zIndex: 1, flex: 1, justifyContent: 'stretch' }} mt={2}>
                            <Select
                              // classNamePrefix="region-select"
                              styles={customStyles}
                              options={HBA1C_UNITS}
                              value={HBA1C_UNITS.find((x) => x.value === values.hbA1cUnit)}
                              onChange={(option) => {
                                if (!option || !option.value) return;
                                setFieldValue('hbA1cUnit', option.value);
                              }}
                            />
                          </Flex>
                        </MobileOnly>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>

                <Flex justifyContent="center" alignItems="center" flexDirection="column" mx={[3, 3, 0]} my={[2, 3]}>
                  <Button disabled={!isValid || this.state.loading || (!values.hbA1c && !values.glucose)} variant="pink" width={[1, 1 / 2, 1 / 4]} type="submit">
                    Next
                  </Button>

                  {data.hasSecondOption && (
                    <Button disabled={this.state.loading} variant="dim" width="auto" type="button" mt={4} onClick={() => this.handleClick(data.options[0])}>
                      {data.secondOptionText}
                    </Button>
                  )}

                  {data.skip && (
                    <StyledText
                      mt={4}
                      onClick={() => {
                        wizard.push(data.skip);
                      }}
                      mb={4}
                    >
                      Skip
                    </StyledText>
                  )}
                </Flex>
              </Form>
            )}
          />
        </Flex>
      </Fragment>
    );
  }
}

export default compose(
  withApollo,
  withWizard,
  withToastManager
)(Diabetes2GlucoseHbA1c);
